/**
 * TanStack Query hook for GET /admin/stats (quick-260909-k4v).
 *
 * API contract:
 *   GET {VITE_API_URL}/admin/stats  (Authorization: Bearer <accessToken>)
 *       -> 200 AdminStatsResponse   (listed admin emails only)
 *       -> 403 for everyone else
 *
 * Reuses apiRequest() from the main app's src/lib/api.ts rather than calling fetch here, so
 * the admin site inherits its token handling for free — the forced-refresh dance for stale
 * tokens, and the signOut-and-throw on a 401 from a deleted user. Both are exactly as
 * relevant here as in the main app.
 *
 * A 403 is modelled as AdminAccessDeniedError rather than a generic failure, because it is
 * not an error condition to retry — it is the expected answer for anyone who is not the
 * owner, and the UI has a specific screen for it.
 */
import { useQuery } from '@tanstack/react-query';
import { apiRequest, AuthSessionExpiredError } from '@/lib/api';

/** Counts for one 30-day window. */
export interface WindowCounts {
  income: number;
  expense: number;
}

export interface AdminUserStats {
  sub: string;
  email?: string;
  /** Cognito UserStatus — EXTERNAL_PROVIDER means the account signed up through Google. */
  status?: string;
  createdAt?: string;
  counts: {
    last30: WindowCounts;
    next30: WindowCounts;
  };
}

export interface AdminStatsResponse {
  generatedAt: string;
  windows: {
    last30: { start: string; end: string };
    next30: { start: string; end: string };
  };
  /** Registered users after exclusions — the headline number. */
  userCount: number;
  /** How many accounts the exclusion rule hid. */
  excludedCount: number;
  users: AdminUserStats[];
}

/** Thrown on a 403 — the signed-in account is not a listed admin. */
export class AdminAccessDeniedError extends Error {
  constructor() {
    super('This account is not authorised to view the admin report.');
    this.name = 'AdminAccessDeniedError';
  }
}

export function useAdminStatsQuery() {
  return useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const response = await apiRequest('/admin/stats', 'GET');
      if (response.status === 403) throw new AdminAccessDeniedError();
      if (!response.ok) {
        throw new Error(`GET /admin/stats failed: ${response.status} ${response.statusText}`);
      }
      return (await response.json()) as AdminStatsResponse;
    },
    // Neither a refused account nor a dead session becomes valid by asking again.
    retry: (failureCount, error) =>
      error instanceof AdminAccessDeniedError || error instanceof AuthSessionExpiredError
        ? false
        : failureCount < 1,
  });
}
